import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Filter, Search, X } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { usePractices } from '../../hooks/usePractices';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { useTeachers } from '../../hooks/useTeachers';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { trainingBillingStatuses } from '../../data/trainingBillingOptions';
import { formatEuro } from '../../utils/currency';
import { practiceTotal, requesterName } from '../../utils/practices';
import { billingStatusVariant, courseAssociation } from '../../utils/trainingBilling';
export function TrainingBillingPage() {
  const navigate = useNavigate();
  const {
    practices
  } = usePractices();
  const records = useTrainingBilling();
  const courses = useTrainingCourses();
  const teachers = useTeachers();
  const [showFilters, setShowFilters] = useState(false);
  const rows = useMemo(() => records.flatMap(r => {
    const practice = practices.find(p => p.id === r.practiceId);
    if (!practice) return [];
    const assoc = courseAssociation(practice, courses, teachers);
    return [{
      ...r,
      id: r.practiceId,
      practice,
      assoc,
      requester: requesterName(practice),
      product: assoc.productLabel,
      productCode: assoc.productCode,
      courseCode: assoc.courseCode || '',
      proformaNumber: r.proforma?.number || '',
      invoiceNumber: r.invoice?.number || '',
      amount: practiceTotal(practice)
    }];
  }), [records, practices, courses, teachers]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(rows);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['requester', 'product', 'productCode', 'courseCode', 'proformaNumber', 'invoiceNumber']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems
  } = usePagination(searchResult);
  const docCell = (doc: {
    progressive: string;
    number?: string;
  } | undefined) => doc ? <div className="whitespace-nowrap" data-id="element-6305" data-mp="3oveuyc7tll1n">
        <p className="text-sm text-slate-800" data-id="element-6306" data-mp="3tnpv9g6h38uq">
          {doc.number || <span className="text-slate-400" data-id="element-6307" data-mp="3tparrbccl1v8">Da emettere</span>}
        </p>
        <p className="text-xs font-mono text-slate-400" data-id="element-6308" data-mp="1i7pbvfr8ox6e">{doc.progressive}</p>
      </div> : <span className="text-sm text-slate-300" data-id="element-6309" data-mp="1d8l48tjyr7i6">—</span>;
  return <Layout data-id="element-6310" data-mp="1r0svy6we48a0">
      <div className="mb-8" data-id="element-6311" data-mp="1r1kkixrkl5qs">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6312" data-mp="1r0j5yjbci14b">
          Fatturazione Formazione
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-6313" data-mp="1mzz6va77onfj">
          Posizioni generate dalle pratiche con proforma o fattura da fare.
        </p>
      </div>

      <Card className="mb-6" noPadding data-id="element-6314" data-mp="ub1svja19csl">
        <div className="p-4 flex flex-col gap-4" data-id="element-6315" data-mp="uc3daecxvbmz">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6316" data-mp="18aji1zr8qs2h">
            <div className="flex-1" data-id="element-6317" data-mp="18buok7c2mdxa">
              <Input placeholder="Cerca richiedente, prodotto, codice corso, n° proforma o fattura..." startContent={<Search className="h-4 w-4" data-id="element-6319" data-mp="2joot1cpvg27b" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-6318" data-mp="13bgi2tq4bc1d" />
            </div>
            <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6321" data-mp="3jyzk8mq65qpx" />} data-id="element-6320" data-mp="3a1bmdnx9o33j">
              Filtri{' '}
              {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6322" data-mp="3oycf9ur7243l">
                  {activeFilterCount}
                </Badge>}
            </Button>
          </div>
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6323" data-mp="1sbgb06uv9lu">
              <Select label="Stato fatturazione" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...trainingBillingStatuses.map(s => ({
            value: s,
            label: s
          }))]} data-id="element-6324" data-mp="6kldn1aavby5" />
              <div className="col-span-full flex justify-end" data-id="element-6325" data-mp="25257xf5595wy">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6327" data-mp="1w1gt4i7xf1j0" />} data-id="element-6326" data-mp="210bcbl9t2s3n">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6328" data-mp="2op2z1x4mmi91">
        <div className="overflow-x-auto" data-id="element-6329" data-mp="2jq9l63lfgp8c">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6330" data-mp="2jrb4swgiek1o">
            <thead data-id="element-6331" data-mp="2ez1bd6g6r1un">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6332" data-mp="2eyi3yjo5vvuv">
                <th className="px-6 py-4" data-id="element-6333" data-mp="37gdm2378eavn">Richiedente</th>
                <th className="px-4 py-4" data-id="element-6334" data-mp="37hyhdv1ddxuk">Corso</th>
                <th className="px-4 py-4 whitespace-nowrap" data-id="element-6335" data-mp="32px88hzi0n8r">Data di svolgimento</th>
                <th className="px-4 py-4" data-id="element-6336" data-mp="32nucdhbnzzw4">Proforma</th>
                <th className="px-4 py-4" data-id="element-6337" data-mp="10tekojni4ev4">Fattura</th>
                <th className="px-4 py-4 text-right whitespace-nowrap" data-id="element-6338" data-mp="14tgq93wcxsfo">
                  Importo senza IVA
                </th>
                <th className="px-4 py-4" data-id="element-6339" data-mp="r18w7jwgkfko">Stato</th>
                <th className="px-6 py-4 text-right" data-id="element-6340" data-mp="r0har1wkry84">
                  <span className="sr-only" data-id="element-6341" data-mp="w0d6yez16ld4">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6342" data-mp="h2krtnxebyzg">
              {paginatedData.length === 0 && <tr data-id="element-6343" data-mp="m4igwysh9o0b">
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-slate-500" data-id="element-6344" data-mp="m0uok2yrr7n6">
                    {rows.length === 0 ? 'Nessuna posizione: segna "Proforma da fare" o "Fattura da fare" in una pratica per generarla.' : 'Nessun risultato con la ricerca o i filtri attuali.'}
                  </td>
                </tr>}
              {paginatedData.map(r => <tr key={r.id} onClick={() => navigate(`/training/fatturazione/${r.id}`)} className="hover:bg-slate-50/80 transition-colors cursor-pointer" data-id="element-6345" data-mp="2hjp83d5nx26g">
                  <td className="px-6 py-4 text-sm font-medium text-seform-navy" data-id="element-6346" data-mp="2qedcl13eyfu7">
                    {r.requester}
                  </td>
                  <td className="px-4 py-4" data-id="element-6347" data-mp="30azw9mk070un">
                    <p className="text-sm text-slate-700" data-id="element-6348" data-mp="309x8hnhz9eiy">{r.product}</p>
                    <p className="text-xs text-slate-400 font-mono" data-id="element-6349" data-mp="2vcev9kwxgd55">
                      {r.productCode}
                      {r.courseCode && ` · ${r.courseCode}`}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-6350" data-mp="359tc3lmrj3xz">
                    {r.assoc.isOnDemand ? <span className="text-slate-400" data-id="element-6351" data-mp="1exx4xty3p3hw">On-demand</span> : r.assoc.dateLabel || '-'}
                  </td>
                  <td className="px-4 py-4" data-id="element-6352" data-mp="1zg6z5hx7vqh5">{docCell(r.proforma)}</td>
                  <td className="px-4 py-4" data-id="element-6353" data-mp="24fjqofz19lq2">{docCell(r.invoice)}</td>
                  <td className="px-4 py-4 text-sm text-right tabular-nums text-slate-700 whitespace-nowrap" data-id="element-6354" data-mp="24dqiuxcoovgh">
                    {r.amount !== undefined ? formatEuro(r.amount) : '-'}
                  </td>
                  <td className="px-4 py-4" data-id="element-6355" data-mp="1gi49r20rbrit">
                    <Badge variant={billingStatusVariant(r.status)} size="sm" className="whitespace-nowrap" data-id="element-6356" data-mp="1gjflbo0g23kh">
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-6357" data-mp="1phr81ey6wh7n">
                    <Link to={`/training/fatturazione/${r.id}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 text-sm font-medium text-seform-orange hover:underline whitespace-nowrap" data-id="element-6358" data-mp="1uhvtljdyg24q">
                      Gestisci <ChevronRight className="h-4 w-4" data-id="element-6359" data-mp="3g1iovbckshm4" />
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6360" data-mp="36axtdws77t7f">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6361" data-mp="369mqs0ahorhw" />
        </div>
      </Card>
    </Layout>;
}